import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import EnhancedTable from "../../../Components/EnhancedTable";
import useAuthStore from "../../../store/useAuthStore";
import Daterange from "./Components/Daterange";
import InvoiceHeader from "./Components/InvoiceHeader";
import { FaArrowRight } from "react-icons/fa";
import Toast from "../../../Components/Toast";

const headCells = [
  { id: "name", numeric: true, disablePadding: true, label: "Invoice Name" },

  { id: "amount", numeric: true, disablePadding: false, label: "Amount (Nu)" },
  {
    id: "items",
    numeric: false,
    disablePadding: false,
    label: "Description",
  },
  { id: "date", numeric: true, disablePadding: false, label: "Creation Date" },
];

const Income = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [entry, setEntry] = useState([]);
  const [toast, setToast] = useState({ show: false, type: "", title: "", message: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [targetIds, setTargetIds] = useState([]);

  // token
  const { token } = useAuthStore();

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        const response = await fetch("/api/getallInvoice", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch invoices");
        }

        const data = await response.json();
        if (data && data.invoice) {
          setEntry(data.invoice);
          console.log("Fetched Data:", data);
        } else {
          console.log("No invoice data found.");
        }

        const formattedData = data.invoice.map((invoice) => {
          // Get the first three items
          const items = invoice.invoiceItems
            .slice(0, 3)
            .map((item) => item.item)
            .join(", ");

          // If there are more than three items, append "..."
          const displayedItems =
            invoice.invoiceItems.length > 3 ? `${items}, ...` : items;
          //  sum of individual amount of the InvoiceItems
          const totalAmount = invoice.invoiceItems.reduce(
            (total, item) => total + (parseFloat(item.amount) || 0),
            0
          );

          return {
            id: invoice._id,
            name: invoice.title,
            client: invoice.to,
            owner: invoice.owner,
            date: invoice.date,
            amount: totalAmount,
            items: displayedItems,
            invoiceItems: invoice.invoiceItems.map((item) => ({
              item: item.item,
              amount: item.amount,
              unitPrice: item.unitPrice,
              qty: item.qty,
            })),
          };
        });

        setRows(formattedData);
        console.log("Formatted Data: ", formattedData);
      } catch (error) {
        console.error("Error fetching invoices: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [token]);

  useEffect(() => { }, [entry]);

  const handleDeleteInvoices = async (selectedIds) => {
    try {
      const response = await fetch("/api/deleteInvoice", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: selectedIds }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to delete Invoice");
      }

      // Remove deleted rows from the local state
      const updatedRows = rows.filter((row) => !selectedIds.includes(row.id));
      setRows(updatedRows);
    } catch (error) {
      console.error("Error deleting Invoice", error);
    }
  };

  // Filter rows based on the selected date range
  const filterRowsByDate = (rows) => {
    if (!startDate || !endDate) return rows;
    return rows.filter((row) => {
      const rowDate = new Date(row.date);
      return rowDate >= startDate && rowDate <= endDate;
    });
  };

  const filteredRows = filterRowsByDate(rows);

  const onRowClick = (entry) => {
    navigate("/Invoice/EditInvoice", { state: { entry } });
  };

  const handleProceedToIncome = async (selectedIds, calculateOnTaxInclusive) => {
    try {
      setIsModalOpen(false);
      const response = await fetch("/api/convert-invoices-to-income", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: selectedIds, calculateOnTaxInclusive }),
      });

      const data = await response.json();
      if (response.ok) {
        setToast({ show: true, type: "success", title: "Success", message: data.message });
        setTimeout(() => window.location.reload(), 2000);
      } else {
        setToast({ show: true, type: "error", title: "Error", message: data.error || "Failed to convert." });
      }
    } catch (error) {
      setToast({ show: true, type: "error", title: "Network Error", message: "Unable to reach server." });
    }
  };

  return (
    <div>
      <div className="bg-gray-100 rounded-md flex items-center">
        <div className="flex-grow">
          <InvoiceHeader />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col space-y-4 mt-8">
          <div className="animate-pulse space-y-2">
            <div className="bg-gray-300 h-6 w-1/4 rounded"></div>
            <div className="bg-gray-300 h-8 w-full rounded"></div>
            <div className="bg-gray-300 h-6 w-3/4 rounded"></div>
            <div className="bg-gray-300 h-8 w-5/6 rounded"></div>
          </div>
          <div className="flex justify-center">
            <div className="animate-pulse rounded-full h-10 w-10 bg-gray-300"></div>
          </div>
        </div>
      ) : (
        <>
          <Daterange
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            visibleRows={filteredRows}
            headCells={headCells}
          />
          <EnhancedTable
            className="rounded-b-none"
            rows={filteredRows}
            headCells={headCells}
            onDelete={handleDeleteInvoices}
            onEditBtn={false}
            onRowClick={onRowClick}
            customActions={(selectedIds) => (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setTargetIds(selectedIds);
                  setIsModalOpen(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all font-bold text-xs uppercase"
              >
                <FaArrowRight />
                Proceed to Income
              </button>
            )}
          />
        </>
      )}

      {toast.show && (
        <Toast
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full border border-gray-100 transform transition-all">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 transition-all">Select Income Calculation</h3>
            <p className="text-gray-600 mb-8 border-l-4 border-emerald-500 pl-4">
              How would you like to record the revenue for the selected invoices?
            </p>
            <div className="flex flex-col gap-4">
              <button
                onClick={() => handleProceedToIncome(targetIds, true)}
                className="w-full py-4 bg-emerald-50 text-emerald-700 font-semibold rounded-lg border-2 border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all flex items-center justify-between px-6"
              >
                <span>With Tax (Total Amount)</span>
                <FaArrowRight className="text-sm opacity-50" />
              </button>
              <button
                onClick={() => handleProceedToIncome(targetIds, false)}
                className="w-full py-4 bg-blue-50 text-blue-700 font-semibold rounded-lg border-2 border-blue-200 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all flex items-center justify-between px-6"
              >
                <span>Without Tax (Subtotal)</span>
                <FaArrowRight className="text-sm opacity-50" />
              </button>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-full py-3 mt-2 text-gray-500 font-medium hover:text-gray-800 transition-colors uppercase text-xs tracking-widest"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Income;
